import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import EmailTone from "@mapmycustomers/shared/enum/email/EmailTone";
import EmailStatus from "@mapmycustomers/shared/enum/EmailStatus";
import { CustomFieldError } from "@mapmycustomers/shared/types/customField/CustomFieldValuesUpsertResponse";
import Draft from "@mapmycustomers/shared/types/draft/Draft";
import EmailDraft from "@mapmycustomers/shared/types/draft/EmailDraft";
import Email from "@mapmycustomers/shared/types/email/Email";
import EmailAssociatedEntity from "@mapmycustomers/shared/types/email/EmailAssociatedEntity";
import EmailAttachment from "@mapmycustomers/shared/types/email/EmailAttachment";
import EmailEntity from "@mapmycustomers/shared/types/email/EmailEntity";
import EmailFile from "@mapmycustomers/shared/types/email/EmailFile";
import EmailPreview from "@mapmycustomers/shared/types/email/EmailPreview";
import EmailQuota from "@mapmycustomers/shared/types/email/EmailQuota";
import EmailSyncProgress from "@mapmycustomers/shared/types/email/EmailSyncProgress";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import GenerateEmailResponse from "@mapmycustomers/shared/types/email/GenerateEmailResponse";
import {
  Deal,
  EntitiesSupportedByEmailFeature,
  EntityType,
  EntityTypesSupportedByEmailFeature,
} from "@mapmycustomers/shared/types/entity";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { RawFile } from "@mapmycustomers/shared/types/File";
import User from "@mapmycustomers/shared/types/User";
import { EmailAttachedFile, ModalFunctions } from "@mapmycustomers/ui";

import {
  CreateEmailDraftPayload,
  CreateEmailTemplatePayload,
  SendEmailPayload,
  UpdateEmailDraftPayload,
  UpdateEmailTemplatePayload,
} from "../api/ApiService";

export const fetchEmailQuota = createAsyncAction(
  "@email/fetchEmailQuota/request",
  "@email/fetchEmailQuota/success",
  "@email/fetchEmailQuota/failure"
)<{ callback?: (quota: EmailQuota) => void } | void, EmailQuota, void>();

export const fetchEmailSyncProgress = createAsyncAction(
  "@email/fetchEmailSyncProgress/request",
  "@email/fetchEmailSyncProgress/success",
  "@email/fetchEmailSyncProgress/failure"
)<void, EmailSyncProgress, void>();

export const fetchEmailDrafts = createAsyncAction(
  "@email/fetchEmailDrafts/request",
  "@email/fetchEmailDrafts/success",
  "@email/fetchEmailDrafts/failure"
)<
  void,
  {
    drafts: EmailDraft[];
    entities: EntitiesSupportedByEmailFeature[];
    resources: Resource[];
  },
  void
>();

export const fetchNewEmailEntities = createAction("@email/fetchNewEmailEntities")<{
  callback: (entities: EntitiesSupportedByEmailFeature[]) => void;
  entityIds: EntitiesSupportedByEmailFeature["id"][];
  entityType: EntityTypesSupportedByEmailFeature;
  failureCallback?: () => void;
}>();

export const fetchSentEmailEntities = createAction("@email/fetchSentEmailEntities")<{
  callback: (params: {
    emailEntities: EntitiesSupportedByEmailFeature[];
    inaccessibleEmailEntities: EmailEntity[];
  }) => void;
  emailId: Email["id"];
  failureCallback?: () => void;
}>();

export const fetchEmailTemplates = createAsyncAction(
  "@email/fetchEmailTemplates/request",
  "@email/fetchEmailTemplates/success",
  "@email/fetchEmailTemplates/failure"
)<void, { resources: Resource[]; templates: EmailTemplate[] }, void>();

export const createEmailTemplate = createAsyncAction(
  "@email/createEmailTemplate/request",
  "@email/createEmailTemplate/success",
  "@email/createEmailTemplate/failure"
)<
  {
    attachments: EmailFile[];
    callback?: (template: EmailTemplate) => void;
    resources: Resource[];
    template: Omit<CreateEmailTemplatePayload, "attachmentIds" | "resourceIds">;
  },
  {
    resources: Resource[];
    template: EmailTemplate;
  },
  void
>();

export const updateEmailTemplate = createAsyncAction(
  "@email/updateEmailTemplate/request",
  "@email/updateEmailTemplate/success",
  "@email/updateEmailTemplate/failure"
)<
  {
    attachments?: EmailFile[];
    callback?: (template: EmailTemplate) => void;
    finishCallback?: () => void;
    resources?: Resource[];
    template: Omit<UpdateEmailTemplatePayload, "attachmentIds" | "metadata" | "resourceIds">;
  },
  {
    resources?: Resource[];
    template: EmailTemplate;
  },
  void
>();

export const deleteEmailTemplate = createAsyncAction(
  "@email/deleteEmailTemplate/request",
  "@email/deleteEmailTemplate/success",
  "@email/deleteEmailTemplate/failure"
)<{ callback?: () => void; templateId: EmailTemplate["id"] }, EmailTemplate["id"], void>();

export const shareEmailTemplate = createAsyncAction(
  "@email/shareEmailTemplate/request",
  "@email/shareEmailTemplate/success",
  "@email/shareEmailTemplate/failure"
)<
  { templateId: EmailTemplate["id"]; userIds: User["id"][] },
  { templateId: EmailTemplate["id"]; userIds: User["id"][] },
  void
>();

export const createEmailDraft = createAsyncAction(
  "@email/createEmailDraft/request",
  "@email/createEmailDraft/success",
  "@email/createEmailDraft/failure"
)<
  {
    attachments: EmailFile[];
    callback?: (draft: EmailDraft) => void;
    displayErrorNotification?: boolean;
    draft: Omit<CreateEmailDraftPayload, "attachmentIds" | "metadata">;
    entities: EntitiesSupportedByEmailFeature[];
    entityType: EntityTypesSupportedByEmailFeature;
    failureCallback?: () => void;
    finishCallback?: () => void;
    resources: Resource[];
  },
  {
    draft: EmailDraft;
    entities: EntitiesSupportedByEmailFeature[];
    resources: Resource[];
  },
  void
>();

export const updateEmailDraft = createAsyncAction(
  "@email/updateEmailDraft/request",
  "@email/updateEmailDraft/success",
  "@email/updateEmailDraft/failure"
)<
  {
    attachments: EmailFile[];
    callback?: (draft: EmailDraft) => void;
    draft: Omit<UpdateEmailDraftPayload, "attachmentIds" | "metadata">;
    entities: EntitiesSupportedByEmailFeature[];
    failureCallback?: () => void;
    finishCallback?: () => void;
    resources: Resource[];
  },
  {
    draft: EmailDraft;
    entities: EntitiesSupportedByEmailFeature[];
    resources: Resource[];
  },
  void
>();

export const deleteEmailDraft = createAsyncAction(
  "@email/deleteEmailDraft/request",
  "@email/deleteEmailDraft/success",
  "@email/deleteEmailDraft/failure"
)<
  {
    callback?: () => void;
    draft: Draft;
    failureCallback?: () => void;
    finishCallback?: () => void;
  },
  void,
  Draft
>();

export const fetchEmailPreview = createAction("@email/fetchEmailPreview")<{
  callback: (emailPreview: EmailPreview) => void;
  email: SendEmailPayload;
  failureCallback: () => void;
}>();

export const fetchEmailAttachment = createAction("@email/fetchEmailAttachment")<{
  attachmentId: EmailAttachment["id"];
  callback: (file: Blob) => void;
  failureCallback: () => void;
  messageId: Email["messageId"];
}>();

export const fetchNewEmailAssociatedEntities = createAction(
  "@email/fetchNewEmailAssociatedEntities"
)<{
  callback?: (emailAssociatedEntities: EmailAssociatedEntity[]) => void;
  deals: Deal[];
  failureCallback?: () => void;
  recipients: EntitiesSupportedByEmailFeature[];
  recipientType: EntityTypesSupportedByEmailFeature;
}>();

export const sendEmail = createAction("@email/sendEmail")<{
  callback?: (email: Email) => void;
  email: SendEmailPayload;
  failureCallback?: (customFieldErrors: CustomFieldError[]) => void;
}>();

export const uploadFileForNewEmail = createAction("@email/uploadFileForNewEmail")<{
  attachedFile: EmailAttachedFile;
  callback?: (attachedFile: EmailAttachedFile) => void;
  failureCallback?: () => void;
}>();

export const uploadImageForNewEmail = createAction("@email/uploadImageForNewEmail")<{
  callback: (url: string) => void;
  file: Blob;
}>();

export const fetchNewEmailPreview = createAction("@email/fetchNewEmailPreview")<{
  callback?: (emailPreview: EmailPreview) => void;
  email: SendEmailPayload;
  failureCallback?: () => void;
}>();

export const fetchEmailQueue = createAsyncAction(
  "@email/fetchEmailQueue/request",
  "@email/fetchEmailQueue/success",
  "@email/fetchEmailQueue/failure"
)<void, Email[], void>();

export const showEmailQueue = createAction("@email/showEmailQueue")<void>();

export const hideEmailQueue = createAction("@email/hideEmailQueue")<void>();

export const clearEmailQueue = createAction("@email/clearEmailQueue")<{
  finishCallback: () => void;
  status?: EmailStatus;
}>();

export const cancelQueuedEmail = createAction("@email/cancelQueuedEmail")<{
  emailId: Email["id"];
  finishCallback: () => void;
}>();

export const checkEmailSupporting = createAction("@email/checkEmailSupporting")<{
  cancelCallback?: () => void;
  modal: Omit<ModalFunctions, "warn">;
  successfulCallback: () => void;
}>();

export const downloadEmailAttachedFile = createAction("@email/downloadAttachedFile")<{
  attachedFile: EmailAttachedFile;
  preview?: boolean;
}>();

export const deleteEmailAttachedFile = createAction("@email/deleteEmailAttachedFile")<
  RawFile["id"]
>();

export const showEmailFeatures = createAction("@email/showEmailFeatures")<void>();

export const hideEmailFeatures = createAction("@email/hideEmailFeatures")<void>();

export const makeEmailResourcePublic = createAction("@email/makeEmailResourcePublic")<{
  callback: (resource: Resource) => void;
  failureCallback?: () => void;
  resourceId: Resource["id"];
}>();

export const subscribeEntitiesEmails = createAction("@email/subscribeEntitiesEmails")<{
  callback?: () => void;
  entities: Pick<EntitiesSupportedByEmailFeature, "entity" | "id">[];
  failureCallback?: () => void;
  finishCallback?: () => void;
}>();

export const unsubscribeEntitiesEmails = createAction("@email/unsubscribeEntitiesEmails")<{
  callback?: () => void;
  entities: Pick<EntitiesSupportedByEmailFeature, "entity" | "id">[];
  failureCallback?: () => void;
  finishCallback?: () => void;
}>();

export const generateEmailContent = createAsyncAction(
  "@email/generateEmailContent/request",
  "@email/generateEmailContent/success",
  "@email/generateEmailContent/failure"
)<
  {
    callback: (data: GenerateEmailResponse) => void;
    email?: {
      body: string;
      subject: string;
    };
    entityId?: EntitiesSupportedByEmailFeature["id"];
    entityType?: EntityType;
    failureCallback: () => void;
    prompt: string;
    regenerate?: boolean;
    signal?: AbortSignal;
    subjectOnly?: boolean;
    tone?: EmailTone;
  },
  void,
  void
>();

export const submitEmailAiFeedback = createAction("@email/submitEmailAiFeedback")<{
  comment?: string;
  requestId: string;
}>();

export type Actions = ActionType<
  | typeof cancelQueuedEmail
  | typeof checkEmailSupporting
  | typeof clearEmailQueue
  | typeof createEmailDraft
  | typeof createEmailTemplate
  | typeof deleteEmailDraft
  | typeof deleteEmailTemplate
  | typeof downloadEmailAttachedFile
  | typeof fetchEmailAttachment
  | typeof fetchEmailDrafts
  | typeof fetchEmailPreview
  | typeof fetchEmailQueue
  | typeof fetchEmailQuota
  | typeof fetchEmailSyncProgress
  | typeof fetchEmailTemplates
  | typeof fetchNewEmailAssociatedEntities
  | typeof fetchNewEmailEntities
  | typeof fetchNewEmailPreview
  | typeof fetchSentEmailEntities
  | typeof generateEmailContent
  | typeof hideEmailFeatures
  | typeof hideEmailQueue
  | typeof makeEmailResourcePublic
  | typeof sendEmail
  | typeof shareEmailTemplate
  | typeof showEmailFeatures
  | typeof showEmailQueue
  | typeof submitEmailAiFeedback
  | typeof subscribeEntitiesEmails
  | typeof unsubscribeEntitiesEmails
  | typeof updateEmailDraft
  | typeof updateEmailTemplate
  | typeof uploadFileForNewEmail
  | typeof uploadImageForNewEmail
>;
